import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Divider } from '@/components/Layout/Divider';

import { MinorAddressFormType } from '@/constants/minor';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';

import { AddressFieldsStack } from '../MinorProofOfAddressDrawer.styled';

const CHILD_LIVES_WITH_TEXT_MAX_LENGTH = 40;

export function MinorLivesWithFields() {
  const { t } = useTranslate();
  const { control, setValue } = useFormContext<MinorAddressFormType>();
  const { childLivesWithRelationshipOptions } =
    useAppSelector(selectConfigsState);
  const relationship = useWatch({
    control,
    name: 'childLivesWithRelationship',
  });

  const translatedRelationshipOptions = useMemo(
    () =>
      (childLivesWithRelationshipOptions || []).map(option => ({
        value: option.value,
        text: option.i18nKey ? t(option.i18nKey) : option.text,
      })),
    [childLivesWithRelationshipOptions, t],
  );

  useEffect(() => {
    if (relationship !== 'OTHER') {
      setValue('childLivesWithRelationshipDescription', '');
    }
  }, [relationship, setValue]);

  if (!childLivesWithRelationshipOptions?.length) {
    return null;
  }

  return (
    <AddressFieldsStack spacing={3}>
      <Controller
        name="childLivesWithPersonName"
        control={control}
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            label={t('minorPoA.childLivesWith.name.label')}
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            inputProps={{ maxLength: CHILD_LIVES_WITH_TEXT_MAX_LENGTH }}
          />
        )}
      />

      <Controller
        name="childLivesWithRelationship"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            control={control}
            options={translatedRelationshipOptions}
            label={t('minorPoA.childLivesWith.relationship.label')}
            placeholder={t('minorPoA.childLivesWith.relationship.placeholder')}
            fullWidth
            useDrawerOnMobile
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      {relationship === 'OTHER' && (
        <Controller
          name="childLivesWithRelationshipDescription"
          control={control}
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label={t('minorPoA.childLivesWith.description.label')}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              inputProps={{ maxLength: CHILD_LIVES_WITH_TEXT_MAX_LENGTH }}
            />
          )}
        />
      )}
      <Divider sx={{ mt: 2, mb: 1 }} width="50%" />
    </AddressFieldsStack>
  );
}
